/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef, useState } from 'react';

import { keyframes } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import EditIcon from '@atlaskit/icon/core/edit';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { ExitingPersistence, Motion, useExitingPersistence } from '@atlaskit/motion';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { MemoryEditInput } from '../memory-edit-input/MemoryEditInput';

const pendingEditsMessage = defineMessage({
	id: 'rovo-memories.memory-subscreen-footer.MemorySubscreenFooter.pending-edits',
	defaultMessage: 'Review your suggested edits',
	description: 'Button label showing how many pending memory edits the user has',
});

const editObservationsMessage = defineMessage({
	id: 'rovo-memories.memory-subscreen-footer.MemorySubscreenFooter.edit-observations',
	defaultMessage: "Update Rovo's observations",
	description: "Button label to open the edit input for Rovo's profile memory observations",
});

const editSuccessMessage = defineMessage({
	id: 'rovo-memories.memory-subscreen-footer.MemorySubscreenFooter.editSuccess',
	defaultMessage: 'Your updates have been added',
	description: 'Inline success message shown after memory observation edits are saved',
});

const slideIn = keyframes({
	'0%': { transform: 'translateY(50%)' },
	'100%': { transform: 'translateY(0)' },
});

const slideOut = keyframes({
	'0%': { transform: 'translateY(0)' },
	'100%': { transform: 'translateY(50%)' },
});

const styles = cssMap({
	root: {
		flexShrink: 0,
		width: '100%',
		position: 'relative',
		paddingTop: token('space.200'),
	},
	overlay: {
		position: 'absolute',
		bottom: '0',
		left: '0',
		right: '0',
		backgroundColor: token('elevation.surface.overlay'),
		paddingTop: token('space.200'),
	},
	actionRow: {
		display: 'grid',
		gridTemplateColumns: 'auto auto',
		justifyContent: 'end',
		alignItems: 'center',
		columnGap: token('space.100'),
		width: '100%',
	},
	secondaryActionSlot: {
		display: 'flex',
		justifyContent: 'end',
		alignItems: 'center',
		minHeight: '32px',
	},
	slideIn: {
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.out.practical'),
		animationName: `${slideIn}`,
	},
	slideOut: {
		animationDuration: token('motion.duration.long'),
		animationTimingFunction: token('motion.easing.in.practical'),
		animationName: `${slideOut}`,
	},
	successMessage: {
		flexShrink: 0,
	},
});

type MemoryEditSubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

interface MemorySubscreenFooterActionsProps {
	pendingEditsCount?: number;
	onReviewPendingEdits: () => void;
	onOpenEditor: () => void;
	submitStatus: MemoryEditSubmitStatus;
}

const MemorySubscreenFooterActions = ({
	pendingEditsCount,
	onReviewPendingEdits,
	onOpenEditor,
	submitStatus,
}: MemorySubscreenFooterActionsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { isExiting } = useExitingPersistence();
	const shouldShowSuccessMessage = submitStatus === 'success' && !isExiting;

	return (
		<div css={styles.actionRow}>
			<div css={styles.secondaryActionSlot}>
				{shouldShowSuccessMessage ? (
					<Inline
						xcss={styles.successMessage}
						space="space.050"
						alignBlock="center"
						role="status"
						aria-live="polite"
					>
						<StatusSuccessIcon label="" color={token('color.icon.success')} size="small" />
						<Text color="color.text.success" size="medium">
							{formatMessage(editSuccessMessage)}
						</Text>
					</Inline>
				) : (
					pendingEditsCount !== undefined &&
					pendingEditsCount > 0 && (
						<Button appearance="default" onClick={onReviewPendingEdits}>
							{formatMessage(pendingEditsMessage)}
						</Button>
					)
				)}
			</div>
			<Button appearance="primary" iconAfter={EditIcon} onClick={onOpenEditor}>
				{formatMessage(editObservationsMessage)}
			</Button>
		</div>
	);
};

export interface MemorySubscreenFooterProps {
	/**
	 * Number of pending memory edits. When provided and > 0, shows the "Review your suggested edits" button.
	 */
	pendingEditsCount?: number;
	/**
	 * Called when the user clicks "Review your suggested edits".
	 */
	onReviewPendingEdits: () => void;
	/**
	 * Called when the user submits a new memory observation via the edit input.
	 */
	onAddObservation: (text: string) => void;
	/**
	 * When true, a save is in flight. Forwarded to the edit input (spinner +
	 * disabled). The editor stays open while submitting and closes once a submit
	 * completes without an error.
	 */
	isSubmitting?: boolean;
	/**
	 * When true, the previous save failed. Forwarded to the edit input to show an
	 * inline error; the editor stays open so the user can retry.
	 */
	hasError?: boolean;
}

export const MemorySubscreenFooter = ({
	pendingEditsCount,
	onReviewPendingEdits,
	onAddObservation,
	isSubmitting = false,
	hasError = false,
}: MemorySubscreenFooterProps): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();
	const [isEditing, setIsEditing] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<MemoryEditSubmitStatus>('idle');

	const wasSubmittingRef = useRef(false);
	useEffect(() => {
		if (isSubmitting) {
			wasSubmittingRef.current = true;
			setSubmitStatus('submitting');
			return;
		}

		if (hasError) {
			wasSubmittingRef.current = false;
			setSubmitStatus('error');
			return;
		}

		if (wasSubmittingRef.current) {
			wasSubmittingRef.current = false;
			setIsEditing(false);
			setSubmitStatus('success');
			return;
		}

		if (submitStatus === 'error') {
			setSubmitStatus('idle');
		}
	}, [isSubmitting, hasError, submitStatus]);

	useEffect(() => {
		if (submitStatus !== 'success') {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			setSubmitStatus((currentStatus) => (currentStatus === 'success' ? 'idle' : currentStatus));
		}, 1000);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [submitStatus]);

	const handleOpenEditor = () => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'addMemoryEdit',
			source: 'memoryModalSubscreen',
		});
		setSubmitStatus('idle');
		setIsEditing(true);
	};

	return (
		<div css={styles.root}>
			<ExitingPersistence exitThenEnter>
				{isEditing ? (
					<Motion
						key="memory-edit-input"
						enteringAnimationXcss={styles.slideIn}
						exitingAnimationXcss={styles.slideOut}
					>
						<MemoryEditInput
							onAdd={onAddObservation}
							onCancel={() => {
								setIsEditing(false);
								setSubmitStatus('idle');
							}}
							isSubmitting={submitStatus === 'submitting'}
							hasError={submitStatus === 'error'}
						/>
					</Motion>
				) : (
					<Motion
						key="memory-edit-trigger"
						enteringAnimationXcss={styles.slideIn}
						exitingAnimationXcss={styles.slideOut}
					>
						<MemorySubscreenFooterActions
							pendingEditsCount={pendingEditsCount}
							onReviewPendingEdits={onReviewPendingEdits}
							onOpenEditor={handleOpenEditor}
							submitStatus={submitStatus}
						/>
					</Motion>
				)}
			</ExitingPersistence>
		</div>
	);
};
